import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import IconifyIcon from "../icon/Icon";
import { useSendReportEmailMutation } from "../../generated/graphql";
import { sweetAlert } from "../../utils/sweetAlert";
import { ExtractedReportData } from "./reportDataExtractor";
import { filterReport } from "./reportFilters";
import SelectParty from "../party/SelectParty";
import AsynchronousSearchForProduct from "../asyncSearch/AsyncSearchForProduct";

export interface SendEmailModalProps {
  open: boolean;
  onClose: () => void;
  reportData: ExtractedReportData;
}

type SelectOption = {
  label: string;
  value: string;
  partyId?: number;
  itemCode?: string;
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SendEmailModal({
  open,
  onClose,
  reportData,
}: SendEmailModalProps) {
  const [partyFilter, setPartyFilter] = useState<SelectOption | null>(null);
  const [itemFilter, setItemFilter] = useState<SelectOption | null>(null);
  const [partyPickerOpen, setPartyPickerOpen] = useState(false);
  const [itemPickerOpen, setItemPickerOpen] = useState(false);
  const filteredReport = useMemo(
    () => filterReport(reportData, partyFilter?.value || "", itemFilter?.value || ""),
    [reportData, partyFilter, itemFilter],
  );
  const [recipientEmail, setRecipientEmail] = useState("");
  const [reportTitle, setReportTitle] = useState("");
  const [attachmentFileName, setAttachmentFileName] = useState("");
  const [emailError, setEmailError] = useState("");
  const [generalError, setGeneralError] = useState("");

  const [sendReportEmailMutation, { loading }] = useSendReportEmailMutation();

  // Reset and prefill defaults whenever modal opens or reportData updates
  useEffect(() => {
    if (open) {
      setPartyFilter(null);
      setItemFilter(null);
      setPartyPickerOpen(false);
      setItemPickerOpen(false);
      const initialTitle = reportData.reportTitle || "Account Report";
      setReportTitle(initialTitle);

      const sanitized = initialTitle
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
      const dateStr = new Date().toISOString().slice(0, 10);
      setAttachmentFileName(`${sanitized || "report"}_${dateStr}.pdf`);

      setEmailError("");
      setGeneralError("");
    }
  }, [open, reportData]);

  const sanitizedFileName = useMemo(() => {
    let name = attachmentFileName.trim();
    if (!name) return "report.pdf";
    if (!name.toLowerCase().endsWith(".pdf")) {
      name += ".pdf";
    }
    return name;
  }, [attachmentFileName]);

  const handleSend = async () => {
    if (loading || !filteredReport.rows.length) return;
    const trimmedEmail = recipientEmail.trim();

    if (!trimmedEmail) {
      setEmailError("Recipient email is required");
      return;
    }

    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setEmailError("Please enter a valid recipient email address (e.g. name@domain.com)");
      return;
    }

    setEmailError("");
    setGeneralError("");

    try {
      const response = await sendReportEmailMutation({
        variables: {
          input: {
            recipientEmail: trimmedEmail,
            reportTitle: reportTitle.trim() || reportData.reportTitle || "Account Report",
            attachmentFilename: sanitizedFileName,
            reportType: reportData.reportType || "REPORT",
            filters: {
              ...reportData.filters,
              partyId: partyFilter?.partyId,
              itemCode: itemFilter?.itemCode,
            },
            columns: reportData.columns,
            rows: filteredReport.rows,
            summaryItems: filteredReport.summaryItems,
          },
        },
      });

      const result = response.data?.sendReportEmail;

      if (result?.success) {
        onClose();
        await sweetAlert(
          `Report successfully emailed to ${trimmedEmail}.\nTransactional delivery initiated via Brevo.`,
          "success",
        );
      } else {
        const errorMsg = result?.message || "Failed to send report email.";
        setGeneralError(errorMsg);
        await sweetAlert(errorMsg, "error");
      }
    } catch (err: any) {
      const msg = err?.message || "An unexpected error occurred while sending email.";
      setGeneralError(msg);
      await sweetAlert(msg, "error");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          width: "100%",
          maxWidth: "640px",
          p: 1,
        },
      }}
    >
      <SelectParty
        open={partyPickerOpen}
        handleClose={() => setPartyPickerOpen(false)}
        selectedParty={partyFilter}
        selectParty={(party: any) => {
          setPartyFilter({
            label: `${party.partyName} (${party.partyCode})`,
            value: party.partyName,
            partyId: party.id,
          });
          setPartyPickerOpen(false);
        }}
      />
      <AsynchronousSearchForProduct
        showProductModal={itemPickerOpen}
        setShowProductModal={setItemPickerOpen}
        selectProduct={undefined}
        onSelectProduct={(item: any) => {
          setItemFilter({
            label: `${item.name} (${item.itemCode})`,
            value: item.name,
            itemCode: item.itemCode,
          });
          setItemPickerOpen(false);
        }}
      />
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconifyIcon icon="mdi:email-send-outline" width={26} height={26} color="#1976d2" />
          <Typography variant="h5" component="span" fontWeight="bold">
            Send Email Receipt / Report
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Email a professional PDF copy of this report directly to your client or accounting records.
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ py: 2 }}>
        {generalError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setGeneralError("")}>
            {generalError}
          </Alert>
        )}

        <Box display="flex" flexDirection="column" gap={1.5}>
          <TextField
            fullWidth label="Selected Party" value={partyFilter?.label || ""}
            placeholder="Click to select a party"
            InputProps={{ readOnly: true }} disabled={loading}
            onClick={() => setPartyPickerOpen(true)}
            sx={{ "& .MuiInputBase-root": { cursor: "pointer" } }}
          />
          <TextField
            fullWidth label="Selected Item" value={itemFilter?.label || ""}
            placeholder="Click to select an item"
            InputProps={{ readOnly: true }} disabled={loading}
            onClick={() => setItemPickerOpen(true)}
            sx={{ "& .MuiInputBase-root": { cursor: "pointer" } }}
          />
          {(partyFilter || itemFilter) && <Typography variant="caption" color="text.secondary">
            {filteredReport.rows.length} matching row{filteredReport.rows.length === 1 ? "" : "s"} will be emailed.
          </Typography>}
          {!filteredReport.rows.length && <Alert severity="warning">No matching rows are available in this report for the selected party or item.</Alert>}
          <TextField
            required
            fullWidth
            autoFocus
            id="recipient-email-input"
            label="Recipient Email Address"
            placeholder="e.g. client@example.com or partner@company.com"
            type="email"
            value={recipientEmail}
            onChange={(e) => {
              setRecipientEmail(e.target.value);
              if (emailError) setEmailError("");
            }}
            error={Boolean(emailError)}
            helperText={emailError || "Required. The report PDF will be delivered to this address."}
            disabled={loading}
          />

          <TextField
            fullWidth
            label="Report Title"
            value={reportTitle}
            onChange={(e) => setReportTitle(e.target.value)}
            disabled={loading}
            helperText="Printed on the PDF banner and included in the email subject."
          />

          <TextField
            fullWidth
            label="Attachment File Name"
            value={attachmentFileName}
            onChange={(e) => setAttachmentFileName(e.target.value)}
            disabled={loading}
            helperText={`Attached as: ${sanitizedFileName}`}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          disabled={!filteredReport.rows.length}
          onClick={handleSend}
          variant="contained"
          color="primary"
          startIcon={<IconifyIcon icon="mdi:send" />}
        >
          Send Email
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
